'use client';

import { FileText, Receipt, Sparkles, ArrowUpRight, FileEdit, Truck, Infinity as InfinityIcon } from 'lucide-react';
import Link from 'next/link';
import { useSubscriptionStatus } from '../hooks/use-billing';

const ADDON_LABELS: Record<string, { label: string; short?: string }> = {
  invoicing_pro: { label: 'Invoicing', short: 'PRO' },
  stock: { label: 'Stoc' },
  reports: { label: 'Rapoarte' },
  api: { label: 'API' },
  e_transport: { label: 'e-Transport' },
  couriers: { label: 'Curieri' },
  accounting: { label: 'Contabilitate' },
  multi_user: { label: 'Multi-user' },
  pos: { label: 'POS' },
  webhooks: { label: 'Webhooks' },
  efactura: { label: 'e-Factura' },
};

function formatAddon(addon: string): { main: string; accent?: string } {
  const entry = ADDON_LABELS[addon];
  if (entry) return { main: entry.label, accent: entry.short };
  return { main: addon.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) };
}

export function UsageWidget() {
  const { data, isLoading } = useSubscriptionStatus();

  if (isLoading || !data) {
    return (
      <article className="relative overflow-hidden rounded-[18px] border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-[0_1px_0_rgba(15,14,15,0.015),0_4px_16px_rgba(15,14,15,0.03)] animate-pulse">
        <div className="px-6 py-5 border-b border-neutral-100 dark:border-neutral-800">
          <div className="h-5 w-40 bg-neutral-100 dark:bg-neutral-800 rounded" />
        </div>
        <div className="px-6 py-5 space-y-3">
          <div className="h-3 w-full bg-neutral-100 dark:bg-neutral-800 rounded" />
          <div className="h-3 w-2/3 bg-neutral-100 dark:bg-neutral-800 rounded" />
        </div>
      </article>
    );
  }

  const { subscription, usage } = data;
  const isUnlimited = usage.invoicesLimit === null;
  const planIsFree = subscription.plan.startsWith('free');
  const addons = (subscription.addons as string[]) || [];
  const availableModules = ((data as any)?.modules?.available as string[] | undefined) ?? [];
  const hasAllModules = availableModules.length > 0 && availableModules.every((m) => addons.includes(m));

  // Userii cu toate modulele disponibile active nu mai vad cardul pe dashboard;
  // detaliile sunt la /setari?tab=billing.
  if (hasAllModules) return null;

  const invoicePct = !isUnlimited && usage.invoicesLimit !== null
    ? Math.min(100, Math.round((usage.invoicesUsed / usage.invoicesLimit) * 100))
    : 0;
  const nearLimit = invoicePct >= 80 && invoicePct < 100;
  const atLimit = invoicePct >= 100;

  return (
    <article
      className="relative overflow-hidden rounded-[18px] border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-[0_1px_0_rgba(15,14,15,0.015),0_4px_16px_rgba(15,14,15,0.03)]"
    >
      {/* Decorative crosshatch corner */}
      <div
        aria-hidden
        className="absolute top-3 right-3 w-8 h-8 opacity-40 pointer-events-none rounded"
        style={{
          backgroundImage:
            'linear-gradient(45deg, transparent 45%, #E8E4D8 46%, #E8E4D8 54%, transparent 55%),' +
            'linear-gradient(-45deg, transparent 45%, #E8E4D8 46%, #E8E4D8 54%, transparent 55%)',
          backgroundSize: '6px 6px',
        }}
      />

      {/* HEADER */}
      <div className="flex items-center justify-between gap-4 px-6 pt-5 pb-4 border-b border-neutral-100 dark:border-neutral-800 flex-wrap">
        <div className="flex items-baseline gap-3 min-w-0">
          <h3
            className="m-0 font-medium tracking-[-0.02em] leading-none text-neutral-900 dark:text-neutral-100"
            style={{ fontFamily: '\"Fraunces\", Georgia, serif', fontSize: '1.375rem' }}
          >
            {(() => {
              const name = subscription.planName || 'Plan curent';
              const parts = name.split(' ');
              if (parts.length === 1) return name;
              const last = parts[parts.length - 1];
              const rest = parts.slice(0, -1).join(' ');
              return (
                <>
                  {rest}{' '}
                  <em
                    style={{
                      fontFamily: '\"Instrument Serif\", serif',
                      fontStyle: 'italic',
                      color: 'rgb(var(--primary-500))',
                      fontWeight: 400,
                    }}
                  >
                    {last}
                  </em>
                </>
              );
            })()}
          </h3>
        </div>

        <div className="flex items-center gap-2">
          {/* Active status pill */}
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6563rem] font-semibold uppercase tracking-[0.08em]"
            style={{
              background: 'rgb(var(--primary-500) / 0.1)',
              color: 'rgb(var(--primary-700))',
              borderWidth: 1,
              borderColor: 'rgb(var(--primary-500) / 0.2)',
              borderStyle: 'solid',
            }}
          >
            <span
              className="h-[5px] w-[5px] animate-pulse rounded-full"
              style={{ background: 'rgb(var(--primary-500))' }}
            />
            activ
          </span>

          {/* Days remaining badge (free_2y only) */}
          {subscription.plan === 'free_2y' && subscription.daysUntilExpire !== null && subscription.daysUntilExpire !== undefined && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-2.5 py-1 text-[0.6563rem] font-medium text-neutral-600 dark:text-neutral-300">
              <span className="font-mono tabular-nums font-semibold">
                {subscription.daysUntilExpire > 60
                  ? Math.floor(subscription.daysUntilExpire / 30) + ' luni'
                  : subscription.daysUntilExpire + ' zile'}
              </span>
              <span className="text-neutral-400">rămase</span>
            </span>
          )}

          {planIsFree && (
            <Link
              href="/preturi"
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-[0.6875rem] font-semibold text-white hover:bg-primary-600 transition-colors"
            >
              Upgrade
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          )}
        </div>
      </div>

      {/* HERO METRIC STRIP */}
      {isUnlimited ? (
        <div
          className="px-6 py-5 flex items-center gap-5"
          style={{
            background:
              'linear-gradient(to right, rgb(var(--primary-500) / 0.04) 0%, transparent 50%)',
          }}
        >
          <div
            className="grid h-12 w-12 shrink-0 place-items-center rounded-xl"
            style={{
              background: 'rgb(var(--primary-500) / 0.12)',
              color: 'rgb(var(--primary-600))',
            }}
          >
            <InfinityIcon className="h-6 w-6" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="font-medium tracking-[-0.02em] leading-none text-neutral-900 dark:text-neutral-100"
              style={{ fontFamily: '\"Fraunces\", Georgia, serif', fontSize: '1.875rem' }}
            >
              Nelimitat
            </div>
            <div className="mt-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-neutral-500">
              facturi · cheltuieli · stoc
            </div>
          </div>
          <div className="hidden sm:block text-right pl-4 border-l border-neutral-200 dark:border-neutral-700">
            <div
              className="font-mono font-semibold tabular-nums text-neutral-900 dark:text-neutral-100 leading-none"
              style={{ fontSize: '1.25rem' }}
            >
              {usage.invoicesUsed}
            </div>
            <div className="mt-1 text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-neutral-500">
              emise luna asta
            </div>
          </div>
        </div>
      ) : (
        <div className="px-6 py-5 space-y-4">
          {/* Invoices */}
          {usage.invoicesLimit !== null && (
            <div>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <div className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-neutral-400" />
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-neutral-500">
                    facturi · luna aceasta
                  </span>
                </div>
                <div className="font-mono tabular-nums text-[0.7188rem]">
                  <span
                    className={
                      atLimit
                        ? 'text-red-600 dark:text-red-400 font-bold'
                        : nearLimit
                          ? 'text-amber-600 dark:text-amber-400 font-bold'
                          : 'text-neutral-900 dark:text-neutral-100 font-bold'
                    }
                  >
                    {usage.invoicesUsed}
                  </span>
                  <span className="text-neutral-400 mx-1">/</span>
                  <span className="text-neutral-500">{usage.invoicesLimit}</span>
                  <span className="ml-2 text-neutral-400">({invoicePct}%)</span>
                </div>
              </div>
              <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: invoicePct + '%',
                    background: atLimit
                      ? '#EF4444'
                      : nearLimit
                        ? '#F59E0B'
                        : 'rgb(var(--primary-500))',
                  }}
                />
              </div>
            </div>
          )}

          {/* Expenses */}
          {usage.expensesLimit !== null && usage.expensesLimit !== undefined && (
            <div>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <div className="flex items-center gap-1.5">
                  <Receipt className="h-3.5 w-3.5 text-neutral-400" />
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-neutral-500">
                    cheltuieli · luna aceasta
                  </span>
                </div>
                <div className="font-mono tabular-nums text-[0.7188rem] font-bold text-neutral-900 dark:text-neutral-100">
                  {usage.expensesUsed}
                  <span className="font-normal text-neutral-400 mx-1">/</span>
                  <span className="font-normal text-neutral-500">{usage.expensesLimit}</span>
                </div>
              </div>
              <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: Math.min(100, Math.round((usage.expensesUsed / usage.expensesLimit) * 100)) + '%',
                    background: 'rgb(var(--primary-400))',
                  }}
                />
              </div>
            </div>
          )}

          {/* Proforme */}
          {usage.proformaLimit !== null && usage.proformaLimit !== undefined && (
            <div>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <div className="flex items-center gap-1.5">
                  <FileEdit className="h-3.5 w-3.5 text-neutral-400" />
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-neutral-500">
                    proforme · luna aceasta
                  </span>
                </div>
                <div className="font-mono tabular-nums text-[0.7188rem] font-bold text-neutral-900 dark:text-neutral-100">
                  {usage.proformaUsed}
                  <span className="font-normal text-neutral-400 mx-1">/</span>
                  <span className="font-normal text-neutral-500">{usage.proformaLimit}</span>
                </div>
              </div>
              <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: Math.min(100, Math.round((usage.proformaUsed / usage.proformaLimit) * 100)) + '%',
                    background: 'rgb(var(--primary-300))',
                  }}
                />
              </div>
            </div>
          )}

          {/* Avize */}
          {usage.avizLimit !== null && usage.avizLimit !== undefined && (
            <div>
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <div className="flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5 text-neutral-400" />
                  <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-neutral-500">
                    avize · luna aceasta
                  </span>
                </div>
                <div className="font-mono tabular-nums text-[0.7188rem] font-bold text-neutral-900 dark:text-neutral-100">
                  {usage.avizUsed}
                  <span className="font-normal text-neutral-400 mx-1">/</span>
                  <span className="font-normal text-neutral-500">{usage.avizLimit}</span>
                </div>
              </div>
              <div className="h-2 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: Math.min(100, Math.round((usage.avizUsed / usage.avizLimit) * 100)) + '%',
                    background: 'rgb(var(--primary-200))',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* MODULE ACTIVE */}
      {addons.length > 0 && (
        <div
          className="px-6 py-4 border-t border-neutral-100 dark:border-neutral-800"
          style={{
            background:
              'linear-gradient(to right, #FFFDF8 0%, #FFFFFF 100%)',
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-3 w-3" style={{ color: 'rgb(var(--primary-500))' }} />
              <span className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-neutral-500">
                Module active
              </span>
            </div>
            <span className="font-mono text-[0.6875rem] font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {addons.length}
            </span>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1.5">
            {addons.map((addon) => {
              const parsed = formatAddon(addon);
              return (
                <div
                  key={addon}
                  className="group relative flex items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-2.5 py-2 transition-all hover:-translate-y-[1px]"
                  style={{ transition: 'all 0.18s ease' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgb(var(--primary-500) / 0.4)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgb(var(--primary-500) / 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{
                      background: 'rgb(var(--primary-500))',
                      boxShadow: '0 0 0 3px rgb(var(--primary-500) / 0.15)',
                    }}
                  />
                  <span className="min-w-0 truncate text-[0.7188rem] font-medium text-neutral-800 dark:text-neutral-200">
                    {parsed.main}
                    {parsed.accent && (
                      <em
                        style={{
                          fontFamily: '\"Instrument Serif\", serif',
                          fontStyle: 'italic',
                          color: 'rgb(var(--primary-500))',
                          fontWeight: 400,
                          marginLeft: 3,
                          fontSize: '1.08em',
                        }}
                      >
                        {parsed.accent}
                      </em>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FOOTER — plan meta */}
      <div className="flex items-center justify-between gap-4 px-6 py-3 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950/50 text-[0.6875rem] flex-wrap">
        <span className="text-neutral-500">
          {planIsFree && subscription.plan === 'free_2y'
            ? 'Plan gratuit ' + (subscription.planName || '') + ' — toate modulele incluse.'
            : 'Plan ' + (subscription.planName || 'activ') + ' · ' + addons.length + ' module incluse'}
        </span>
        <Link
          href="/setari?tab=billing"
          className="inline-flex items-center gap-1 font-medium text-neutral-700 dark:text-neutral-300 border-b border-dotted border-neutral-300 dark:border-neutral-600 hover:text-primary hover:border-primary transition"
        >
          Detalii plan
          <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
    </article>
  );
}
